import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import CustomTable from "../../../../components/custom-table/custom-table";
import { createDataForWriteBackTrail } from "../../../../components/modals/imageViewerWithBadgesModal/utils/createDataForWriteBackTrail";
import { DOC_SIZE } from "../../../../constants/medicalPAbatch";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { TableNames } from "../../../../enums/tableNames";
import { fetchCountsByMasterAuthStatusByAlgolia } from "../../../../redux/slice/medicalPa/algoliaFacetsCountSlice";
import {
  fetchGenericAuthStatusTableData,
  fetchGenericAuthStatusTableDataAfterSearch,
  fetchMoreGenericAuthStatusTableData,
  hasMoreDataToLoad,
  resetPagination,
  setLoadingState,
} from "../../../../redux/slice/medicalPa/genericAuthStatusTableSlice";
import {
  setMetaData,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import {
  exportDataFromTableForMedicalPaOrders,
  exportDataToExcel,
} from "../../../../utils/exportDataToExcel";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { getFilePathsForModal } from "../../external/allPaOrdersForExternal/utils/allPaOrdersForExternalHelper";
import {
  generateGenericAuthStatusTableData,
  getTableHeaders,
} from "../utils/genericAuthStatusTableDataGenerator";
const GenericAuthStatusOrdersTable = ({
  genericAuthStatusOrdersTableRef,
  tabId,
}: {
  genericAuthStatusOrdersTableRef: React.RefObject<HTMLDivElement>;
  tabId?: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const {
    genericAuthStatusTableData,
    loading,
    showinLineLoader,
    totalDocuments,
    currentlyLoadedCount,
  } = useSelector((state: RootState) => state.genericAuthStatusTable);

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const [tablesData, setTablesData] = useState<GenericAuthStatusTableData[]>(
    [],
  );

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isTableRefActive, setIsTableRefActive] = useState(false);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext(
    `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
  );

  const headers = getTableHeaders(tabId ?? "");

  const navigate = useNavigate();
  const docSize = DOC_SIZE;

  const handleNavigateToNextPage = (rowData: any) => {
    dispatch(resetPagination());
    const data = JSON.parse(rowData.rowData as string) as MedicalPaOrder;
    apiWorkerManager.enqueueAndProcess({
      patient_id: data?.payload?.patient_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist(),
      order_id: rowData.id,
      event_name: EventsName.ORDER_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
        source: tabId ?? "",
        payer_name: data?.coverage?.primary?.payer_name ?? "",
      },
    });
    navigate(`/worklists/evbv/${rowData["id"]}`);
  };

  const exportDataForTable = () => {
    const data = exportDataFromTableForMedicalPaOrders(
      genericAuthStatusTableData ?? [],
    );
    exportDataToExcel(
      data,
      `genericAuthStatusOrders-${moment().format("YYYY-MM-DD")}`,
    );
  };

  const initialActivityBeforeRefreshingData = async () => {
    dispatch(setLoadingState(true));
    await new Promise((resolve) => setTimeout(resolve, 5000));
    dispatch(setLoadingState(false));
    dispatch(
      fetchCountsByMasterAuthStatusByAlgolia({
        org_id: getOrgIdForFetchExternalWorklist(),
        facet_attribute: "master_auth_status",
        assigned_to: providerDetails?.DocID ?? "",
      }),
    );
  };

  const handleRefreshButtonClick = async () => {
    await initialActivityBeforeRefreshingData();
    if (searchTerm === "") {
      const isLastBatchFetchedIsGreaterThanZero =
        tableAttributes?.lastBatchFetched &&
        tableAttributes?.lastBatchFetched > 0;
      const pageSize = isLastBatchFetchedIsGreaterThanZero
        ? (tableAttributes?.lastBatchFetched ?? 1) * docSize
        : docSize;
      dispatch(
        fetchGenericAuthStatusTableData(
          providerDetails?.FacilityId ?? "",
          pageSize,
          providerDetails?.DocID ?? "",
          providerDetails?.IsAdmin ?? false,
          tabId ?? "",
        ),
      );
    } else {
      dispatch(
        fetchGenericAuthStatusTableDataAfterSearch(
          providerDetails?.FacilityId ?? "",
          providerDetails?.DocID ?? "",
          tabId ?? "",
          searchTerm,
          providerDetails?.IsAdmin ?? false,
        ),
      );
    }
  };

  useEffect(() => {
    if (
      providerDetails?.FacilityId &&
      providerDetails?.DocID &&
      searchTerm === "" &&
      isTableRefActive &&
      tabId
    ) {
      const isLastBatchFetchedIsGreaterThanZero =
        tableAttributes?.lastBatchFetched &&
        tableAttributes?.lastBatchFetched > 0;
      const pageSize = isLastBatchFetchedIsGreaterThanZero
        ? (tableAttributes?.lastBatchFetched ?? 1) * docSize
        : docSize;
      dispatch(
        fetchGenericAuthStatusTableData(
          providerDetails.FacilityId ?? "",
          pageSize,
          providerDetails?.DocID ?? "",
          providerDetails?.IsAdmin ?? false,
          tabId ?? "",
        ),
      );
    } else if (
      providerDetails?.FacilityId &&
      providerDetails?.DocID &&
      searchTerm !== "" &&
      isTableRefActive &&
      tabId
    ) {
      dispatch(
        fetchGenericAuthStatusTableDataAfterSearch(
          providerDetails?.FacilityId ?? "",
          providerDetails?.DocID ?? "",
          tabId ?? "",
          searchTerm,
          providerDetails?.IsAdmin ?? false,
        ),
      );
    }
  }, [
    providerDetails?.FacilityId,
    providerDetails?.DocID,
    searchTerm,
    isTableRefActive,
    tabId,
  ]);

  useEffect(() => {
    if (!loading && genericAuthStatusTableData) {
      setTablesData(
        generateGenericAuthStatusTableData(
          genericAuthStatusTableData,
          authStatusOptions,
        ),
      );
    }
  }, [loading, genericAuthStatusTableData]);

  useEffect(() => {
    if (genericAuthStatusOrdersTableRef.current) {
      setIsTableRefActive(true);
    }
  }, [genericAuthStatusOrdersTableRef]);

  const handleEndIndexOfTable = (endIndex: number) => {
    const batchNumber = Math.ceil(currentlyLoadedCount / docSize);
    setTableDataForContext(
      `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
      {
        lastBatchFetched: batchNumber,
      },
    );
    const hasMore = hasMoreDataToLoad(
      {
        genericAuthStatusTable: {
          currentlyLoadedCount,
          totalDocuments,
        },
      },
      endIndex,
    );

    if (
      hasMore &&
      !loading &&
      !showinLineLoader &&
      providerDetails?.FacilityId &&
      tabId
    ) {
      dispatch(
        fetchMoreGenericAuthStatusTableData(
          providerDetails.FacilityId ?? "",
          docSize,
          providerDetails?.DocID ?? "",
          providerDetails?.IsAdmin ?? false,
          tabId ?? "",
        ),
      );
    }
  };

  const handlePropagateEventOnClickInRpaStatusCell = async (id: string) => {
    try {
      dispatch(
        setOpenedModalId({
          id: ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL,
          metaData: {
            showLoading: true,
          },
        }),
      );
      const dataForWriteBackTrail = await createDataForWriteBackTrail(
        id,
        authStatusOptions,
      );
      dispatch(
        setMetaData({
          tabs: dataForWriteBackTrail.tabs,
          showLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setMetaData({
          tabs: [],
          showLoading: false,
        }),
      );
    }
  };

  const handlePropagateEventOnClickInRpaStatusTwoCell = async (id: string) => {
    dispatch(
      setOpenedModalId({
        id: ModalId.IMAGE_VIEWER_MODAL,
        metaData: { showLoader: true },
      }),
    );

    try {
      const document = (await FirestoreService.getDocument(
        `/medical_pa_orders/${id}`,
      )) as MedicalPaOrder;
      const filePaths = getFilePathsForModal(document);
      dispatch(
        setMetaData({
          filePaths: filePaths,
          showLoader: false,
        }),
      );
    } catch (error) {
      // Error handled silently
    }
  };

  return (
    <>
      <div ref={genericAuthStatusOrdersTableRef} className="h-full">
        <CustomTable
          tableHeaders={headers}
          tableData={tablesData}
          tableName={`${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`}
          itemsPerPage={10}
          pagesPerView={4}
          count={tablesData.length}
          totalCount={totalDocuments}
          endIndexOfTable={handleEndIndexOfTable}
          searchingText={(searchText: string) => {
            setSearchTerm(searchText);
          }}
          hideSearchBar={false}
          onRowClick={(rowData: any) => {
            handleNavigateToNextPage(rowData);
          }}
          isFetching={loading}
          showExportButton={true}
          onExportButtonClick={exportDataForTable}
          showInLineLoader={showinLineLoader}
          showRefreshButton={true}
          onRefreshButton={handleRefreshButtonClick}
          isDisabled={loading}
          onSideModalClose={() => {
            handleRefreshButtonClick();
          }}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusCell(id);
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusTwoCell(id);
          }}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.WORKLIST}
        />
      </div>
    </>
  );
};

export default GenericAuthStatusOrdersTable;
